import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | undefined} */
  #observer;
  #motion = matchMedia('(prefers-reduced-motion: reduce)');
  #intersecting = false;
  /** @type {AbortController | undefined} */
  #loading;

  #loadWhenVisible = () => {
    if (!this.#intersecting || this.#motion.matches) return;
    this.#observer?.disconnect();
    this.#motion.removeEventListener('change', this.#loadWhenVisible);
    this.#loadSources();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#intersecting = false;
    this.#motion.addEventListener('change', this.#loadWhenVisible);

    // The preview image holds the frame, so the file waits until the video is on screen. A margin
    // would pull in a PLP promo tile that starts just below a desktop fold (12 MB on page load).
    this.#observer = new IntersectionObserver((entries) => {
      this.#intersecting = entries[entries.length - 1]?.isIntersecting ?? false;
      this.#loadWhenVisible();
    });

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#loading?.abort();
    this.#observer?.disconnect();
    this.#motion.removeEventListener('change', this.#loadWhenVisible);
  }

  async #loadSources() {
    const { videoSources, videoElement } = this.refs;
    const loading = (this.#loading = new AbortController());

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    await import('@theme/custom.video-rendition')
      .then(({ preferRendition }) => preferRendition(videoElement, undefined, loading.signal))
      .catch(() => {});
    // A disconnect during the choice leaves the video to the next connect's fresh intersection.
    if (!loading.signal.aborted) videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
